/**
 * לוח התראות "מחיר בסיס עלה" — TASKS 5.4, PRD 7.3.
 *
 * ⛔ ⛔ **המערכת לעולם לא משנה מחיר מעצמה.** שלוש הפעולות היחידות: "עדכן
 *    הכול" · "עדכן נבחרים" (רק אחרי בחירה מפורשת) · "השאר" (dismiss).
 */
import { useEffect, useState } from 'react';
import { isAdminApiError } from './adminApi.js';
import {
  getPriceAlertAffectedCustomers,
  listPriceAlerts,
  resolvePriceAlert,
  type AffectedCustomer,
  type PriceChangeAlert,
} from './priceAlertsApi.js';

export function PriceAlertsPanel() {
  const [alerts, setAlerts] = useState<PriceChangeAlert[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [affected, setAffected] = useState<AffectedCustomer[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [busyId, setBusyId] = useState<number | null>(null);

  async function refetch() {
    try {
      const result = await listPriceAlerts('open');
      setAlerts(result.alerts);
    } catch (err) {
      setError(isAdminApiError(err) ? err.message : 'שגיאה בטעינת התראות מחיר.');
    }
  }

  useEffect(() => {
    void refetch();
  }, []);

  async function handleExpand(alert: PriceChangeAlert) {
    if (expandedId === alert.id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(alert.id);
    setSelectedIds(new Set());
    try {
      const result = await getPriceAlertAffectedCustomers(alert.id);
      setAffected(result.customers);
    } catch (err) {
      setError(isAdminApiError(err) ? err.message : 'שגיאה בטעינת הלקוחות המושפעים.');
    }
  }

  function toggleSelected(customerId: number) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(customerId)) next.delete(customerId);
      else next.add(customerId);
      return next;
    });
  }

  async function handleResolve(
    alertId: number,
    action: 'update_all' | 'update_selected' | 'dismiss',
  ) {
    setError(null);
    setBusyId(alertId);
    try {
      await resolvePriceAlert(
        alertId,
        action,
        action === 'update_selected' ? [...selectedIds] : undefined,
      );
      setExpandedId(null);
      await refetch();
    } catch (err) {
      setError(isAdminApiError(err) ? err.message : 'שגיאה בטיפול בהתראה.');
    } finally {
      setBusyId(null);
    }
  }

  if (alerts === null || alerts.length === 0) {
    return error ? (
      <p role="alert" className="text-danger mt-6 text-sm">
        {error}
      </p>
    ) : null;
  }

  return (
    // ⭐ DESIGN.md 8.2 — "מסגרת נחושת בולטת בראש הדשבורד... לא אדום, זו התראה
    // תפעולית, לא שגיאה." ⛔ ⛔ הרקע ⛔ נשאר `surface` — נחושת מלאה על טקסט
    // נכשלת בניגודיות (index.css, ליד `--color-copper`), מסגרת בלבד עוברת.
    <section
      className="border-copper bg-surface mt-6 rounded-lg border-2 p-6"
      aria-labelledby="price-alerts-h"
    >
      <h2 id="price-alerts-h" className="text-lg font-semibold">
        התראות מחיר בסיס ({alerts.length})
      </h2>
      <p className="text-muted mt-1 text-sm">
        מחיר הבסיס עלה מעל מחיר מיוחד שמור ללקוח קבוע. המערכת לא משנה כלום מעצמה.
      </p>

      {error && (
        <p role="alert" className="text-danger mt-3 text-sm">
          {error}
        </p>
      )}

      <ul className="mt-4 space-y-3">
        {alerts.map((alert) => (
          <li key={alert.id} className="border-line rounded border p-3 text-sm">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span>
                <span className="ltr-inline font-semibold">{alert.product_sku}</span> -{' '}
                {alert.product_name}
                {alert.variant_label && <> · {alert.variant_label}</>}
                {' - '}
                <span className="tabular">
                  ₪{alert.old_base_price} → ₪{alert.new_base_price}
                </span>
                <span className="text-muted">
                  {' '}
                  ({alert.affected_customers_count} לקוחות מושפעים בעת הפתיחה)
                </span>
              </span>

              <span className="flex items-center gap-2">
                <button
                  type="button"
                  className="btn btn-cta"
                  disabled={busyId === alert.id}
                  onClick={() => void handleResolve(alert.id, 'update_all')}
                >
                  עדכן הכול
                </button>
                <button
                  type="button"
                  className="rounded border border-line px-2 py-1"
                  disabled={busyId === alert.id}
                  onClick={() => void handleExpand(alert)}
                >
                  {expandedId === alert.id ? 'סגירה' : 'עדכן נבחרים'}
                </button>
                <button
                  type="button"
                  className="rounded border border-line px-2 py-1"
                  disabled={busyId === alert.id}
                  onClick={() => void handleResolve(alert.id, 'dismiss')}
                >
                  השאר
                </button>
              </span>
            </div>

            {expandedId === alert.id && (
              <div className="border-line mt-3 border-t pt-3">
                {affected.length === 0 && (
                  <p className="text-muted text-sm">אין כרגע לקוחות עם מחיר נמוך מהבסיס החדש.</p>
                )}
                {affected.length > 0 && (
                  <>
                    <ul className="space-y-1">
                      {affected.map((customer) => (
                        <li key={customer.customer_id}>
                          <label className="flex items-center gap-2 text-sm">
                            <input
                              type="checkbox"
                              checked={selectedIds.has(customer.customer_id)}
                              onChange={() => toggleSelected(customer.customer_id)}
                            />
                            {customer.full_name} -{' '}
                            <span className="ltr-inline">{customer.phone_e164}</span> -{' '}
                            <span className="tabular">₪{customer.fixed_price}</span>
                          </label>
                        </li>
                      ))}
                    </ul>
                    <button
                      type="button"
                      className="btn btn-secondary mt-3"
                      disabled={busyId === alert.id || selectedIds.size === 0}
                      onClick={() => void handleResolve(alert.id, 'update_selected')}
                    >
                      עדכן {selectedIds.size || ''} נבחרים
                    </button>
                  </>
                )}
              </div>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
